import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Paper,
    Typography,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUICustomDataGrid } from '../../atomic/molecules';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Code as CodeIcon,
    Category as CategoryIcon,
    SmartToy as SmartToyIcon,
    Psychology as PsychologyIcon,
    AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SCUIContent } from '../../atomic/organisms';
import { SCUIButton } from '../../atomic/atoms';
import TimeHelper from '../../utils/time';
import { GetAIPrompts, AIPromptResponse } from '../../services/query/aiPrompt/AIPromptQuery.services';
import {
    CreateAIPrompt,
    UpdateAIPrompt,
    DeleteAIPrompt,
    CreateAIPromptRequest,
    UpdateAIPromptRequest,
} from '../../services/command/aiPrompt/AIPromptCommand.services';
import AIPromptFormDrawer, { AIPromptFormData } from './components/AIPromptFormDrawer';
import { AIPromptCategory, AIPromptCategoryOptions, AIModelTypeOptions } from '../../types/enums/ai-prompt-enums';

const AIPromptManagement: React.FC = () => {
    const { t } = useTranslation();
    const [prompts, setPrompts] = useState<AIPromptResponse[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [editData, setEditData] = useState<AIPromptResponse | null>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState<boolean>(false);
    const [detailData, setDetailData] = useState<AIPromptResponse | null>(null);

    // Filters
    const [categoryFilter, setCategoryFilter] = useState<AIPromptCategory | ''>('');
    const [activeFilter, setActiveFilter] = useState<string>('all');

    const loadPrompts = useCallback(async () => {
        try {
            setLoading(true);
            const params: any = {};

            if (categoryFilter) params.category = categoryFilter;
            if (activeFilter === 'active') params.isActive = true;
            if (activeFilter === 'inactive') params.isActive = false;

            const response = await GetAIPrompts(params);

            // Response handling: axios-wrapper direkt data döndürür
            if (Array.isArray(response)) {
                setPrompts(response);
            } else if (response && typeof response === 'object' && 'data' in response) {
                // Fallback: response.data varsa onu kullan
                setPrompts((response as any).data || []);
            } else {
                setPrompts([]);
            }
        } catch (error: any) {
            console.error('Error loading prompts:', error);
            setPrompts([]);
            Swal.fire(
                t('aiPrompt.messages.error'),
                error?.response?.data?.message || error?.message || t('aiPrompt.messages.loadError'),
                'error'
            );
        } finally {
            setLoading(false);
        }
    }, [categoryFilter, activeFilter, t]);

    useEffect(() => {
        loadPrompts();
    }, [loadPrompts]);

    const handleOpenDrawer = (data?: AIPromptResponse) => {
        setEditData(data || null);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setEditData(null);
    };

    const handleSubmit = async (data: AIPromptFormData) => {
        try {
            setLoading(true);

            if (editData) {
                // Update
                const updateRequest: UpdateAIPromptRequest = {
                    name: data.name,
                    code: data.code,
                    category: data.category,
                    modelType: data.modelType,
                    promptText: data.promptText,
                    isSystemPrompt: data.isSystemPrompt,
                    isActive: data.isActive,
                };
                await UpdateAIPrompt(editData.id, updateRequest);
                Swal.fire(t('aiPrompt.messages.success'), t('aiPrompt.messages.updateSuccess'), 'success');
            } else {
                // Create
                const createRequest: CreateAIPromptRequest = {
                    name: data.name,
                    code: data.code,
                    category: data.category,
                    modelType: data.modelType,
                    promptText: data.promptText,
                    isSystemPrompt: data.isSystemPrompt,
                    isActive: data.isActive,
                };
                await CreateAIPrompt(createRequest);
                Swal.fire(t('aiPrompt.messages.success'), t('aiPrompt.messages.createSuccess'), 'success');
            }

            handleCloseDrawer();
            loadPrompts();
        } catch (error: any) {
            const errorMessage = editData ? t('aiPrompt.messages.updateError') : t('aiPrompt.messages.createError');
            Swal.fire(t('aiPrompt.messages.error'), errorMessage, 'error');
            console.error('Error submitting prompt:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDeleteDialog = async (id: number) => {
        const result = await Swal.fire({
            title: t('aiPrompt.delete'),
            text: t('aiPrompt.deleteConfirm'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('aiPrompt.delete'),
            cancelButtonText: t('general.cancel'),
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                await DeleteAIPrompt(id);
                Swal.fire(t('aiPrompt.messages.success'), t('aiPrompt.messages.deleteSuccess'), 'success');
                loadPrompts();
            } catch (error: any) {
                Swal.fire(t('aiPrompt.messages.error'), t('aiPrompt.messages.deleteError'), 'error');
                console.error('Error deleting prompt:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleOpenDetailDialog = (data: AIPromptResponse) => {
        setDetailData(data);
        setDetailDialogOpen(true);
    };

    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
        setDetailData(null);
    };

    const columns: ExtendedGridColDef[] = [
        {
            field: 'id',
            headerName: 'ID',
            width: 80,
        },
        {
            field: 'name',
            headerName: t('aiPrompt.name'),
            flex: 1,
            minWidth: 200,
            maxWidth: 300,
            renderCell: (params: GridRenderCellParams) => (
                <p
                    style={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        lineHeight: '1.5',
                        padding: '1px',
                    }}
                >
                    {params.value}
                </p>
            ),
        },
        {
            field: 'code',
            headerName: t('aiPrompt.code'),
            width: 180,
            maxWidth: 200,
            renderCell: (params: GridRenderCellParams) => (
                <Box
                    sx={{
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        lineHeight: '1.5',
                        py: 1,
                    }}
                >
                    {params.value}
                </Box>
            ),
        },
        {
            field: 'category',
            headerName: t('aiPrompt.category'),
            width: 150,
            renderCell: (params: GridRenderCellParams) => {
                const categoryOption = AIPromptCategoryOptions.find((opt) => opt.value === params.value);
                return categoryOption ? t(categoryOption.label) : params.value;
            },
        },
        {
            field: 'createdDate',
            headerName: t('aiPrompt.createdDate'),
            width: 180,
            valueFormatter: (params: any) => {
                return params?.value ? TimeHelper.formatDateHourWithTimezone(params.value) : '';
            },
        },
        {
            field: 'actions',
            headerName: t('general.actions'),
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <IconButton
                        size="small"
                        color="info"
                        onClick={() => handleOpenDetailDialog(params.row as AIPromptResponse)}
                        title={t('general.detail')}
                    >
                        <VisibilityIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDrawer(params.row as AIPromptResponse)}
                        title={t('general.edit')}
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(params.row.id)}
                        title={t('general.delete')}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <SCUIContent>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4">{t('aiPrompt.title')}</Typography>
                    <SCUIButton
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('aiPrompt.addNew')}
                        showStartIcon={true}
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDrawer()}
                    />
                </Box>

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>{t('aiPrompt.category')}</InputLabel>
                        <Select
                            value={categoryFilter}
                            label={t('aiPrompt.category')}
                            onChange={(e) => setCategoryFilter(e.target.value as AIPromptCategory | '')}
                        >
                            <MenuItem value="">
                                <em>{t('aiPrompt.filters.all')}</em>
                            </MenuItem>
                            {AIPromptCategoryOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t(option.label)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel>{t('general.status')}</InputLabel>
                        <Select
                            value={activeFilter}
                            label={t('general.status')}
                            onChange={(e) => setActiveFilter(e.target.value)}
                        >
                            <MenuItem value="all">{t('aiPrompt.filters.all')}</MenuItem>
                            <MenuItem value="active">{t('aiPrompt.filters.activeOnly')}</MenuItem>
                            <MenuItem value="inactive">{t('general.passive')}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Data Grid */}
                <SCUICustomDataGrid
                    rows={prompts}
                    columns={columns}
                    loading={loading}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    selectionOnClick={true}
                    getRowHeight={() => 'auto'}
                    sx={{
                        '& .MuiDataGrid-cell': {
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            whiteSpace: 'normal !important',
                            wordWrap: 'break-word',
                            lineHeight: '1.5',
                        },
                        '& .MuiDataGrid-row': {
                            maxHeight: 'none !important',
                        },
                    }}
                />
            </Paper>

            {/* Form Drawer */}
            <AIPromptFormDrawer
                open={drawerOpen}
                onClose={handleCloseDrawer}
                onSubmit={handleSubmit}
                editData={editData}
                loading={loading}
            />

            {/* Detail Dialog */}
            <Dialog
                open={detailDialogOpen}
                onClose={handleCloseDetailDialog}
                maxWidth="md"
                fullWidth
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        },
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        borderBottom: '1px solid #e0e0e0',
                        pb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <VisibilityIcon color="primary" />
                    <Typography variant="h6" component="span">
                        {t('aiPrompt.detail')}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ mt: 3 }}>
                    {detailData && (
                        <Box>
                            {/* Header Section with Status Badges */}
                            <Box
                                sx={{
                                    mb: 4,
                                    p: 3,
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: 2,
                                    border: '1px solid #e9ecef',
                                }}
                            >
                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#212529' }}>
                                    {detailData.name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                                    <Chip
                                        label={detailData.isActive ? t('general.active') : t('general.passive')}
                                        color={detailData.isActive ? 'success' : 'default'}
                                        size="small"
                                        sx={{ fontWeight: 500 }}
                                    />
                                    <Chip
                                        label={
                                            detailData.isSystemPrompt
                                                ? t('aiPrompt.systemPrompt')
                                                : t('aiPrompt.userPrompt')
                                        }
                                        color={detailData.isSystemPrompt ? 'info' : 'default'}
                                        size="small"
                                        sx={
                                            detailData.isSystemPrompt
                                                ? {
                                                      backgroundColor: '#1976d2',
                                                      color: '#fff',
                                                      fontWeight: 500,
                                                  }
                                                : { fontWeight: 500 }
                                        }
                                    />
                                </Box>
                            </Box>

                            {/* Information Grid */}
                            <Box sx={{ display: 'grid', gap: 3 }}>
                                {/* Code */}
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <CodeIcon fontSize="small" sx={{ color: '#6c757d' }} />
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                color: '#6c757d',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                fontSize: '0.75rem',
                                                letterSpacing: '0.5px',
                                            }}
                                        >
                                            {t('aiPrompt.code')}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontFamily: 'monospace',
                                            backgroundColor: '#f8f9fa',
                                            p: 1.5,
                                            borderRadius: 1,
                                            border: '1px solid #dee2e6',
                                            color: '#495057',
                                        }}
                                    >
                                        {detailData.code}
                                    </Typography>
                                </Box>

                                {/* Category and Model Type Row */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <CategoryIcon fontSize="small" sx={{ color: '#6c757d' }} />
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: '#6c757d',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    fontSize: '0.75rem',
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                {t('aiPrompt.category')}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#212529' }}>
                                            {t(
                                                AIPromptCategoryOptions.find((opt) => opt.value === detailData.category)
                                                    ?.label || ''
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <SmartToyIcon fontSize="small" sx={{ color: '#6c757d' }} />
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: '#6c757d',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    fontSize: '0.75rem',
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                {t('aiPrompt.modelType')}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#212529' }}>
                                            {t(
                                                AIModelTypeOptions.find((opt) => opt.value === detailData.modelType)
                                                    ?.label || ''
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Prompt Text */}
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <PsychologyIcon fontSize="small" sx={{ color: '#6c757d' }} />
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                color: '#6c757d',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                fontSize: '0.75rem',
                                                letterSpacing: '0.5px',
                                            }}
                                        >
                                            {t('aiPrompt.promptText')}
                                        </Typography>
                                    </Box>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2.5,
                                            backgroundColor: '#f8f9fa',
                                            border: '1px solid #dee2e6',
                                            borderRadius: 1,
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                whiteSpace: 'pre-wrap',
                                                lineHeight: 1.8,
                                                color: '#495057',
                                            }}
                                        >
                                            {detailData.promptText}
                                        </Typography>
                                    </Paper>
                                </Box>

                                {/* Timestamps */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 1fr',
                                        gap: 3,
                                        pt: 2,
                                        borderTop: '1px solid #e9ecef',
                                    }}
                                >
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <AccessTimeIcon fontSize="small" sx={{ color: '#6c757d' }} />
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: '#6c757d',
                                                    fontWeight: 600,
                                                    textTransform: 'uppercase',
                                                    fontSize: '0.75rem',
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                {t('aiPrompt.createdDate')}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                            {TimeHelper.formatDateHourWithTimezone(detailData.createdDate)}
                                        </Typography>
                                    </Box>
                                    {detailData.updatedDate && (
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                <AccessTimeIcon fontSize="small" sx={{ color: '#6c757d' }} />
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        color: '#6c757d',
                                                        fontWeight: 600,
                                                        textTransform: 'uppercase',
                                                        fontSize: '0.75rem',
                                                        letterSpacing: '0.5px',
                                                    }}
                                                >
                                                    {t('aiPrompt.updatedDate')}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                                {TimeHelper.formatDateHourWithTimezone(detailData.updatedDate)}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #e0e0e0', px: 3, py: 2 }}>
                    <SCUIButton
                        type="secondary"
                        sizeType="default"
                        size="m"
                        text={t('general.close')}
                        onClick={handleCloseDetailDialog}
                    />
                </DialogActions>
            </Dialog>
        </SCUIContent>
    );
};

export default AIPromptManagement;
